"use client";

import { getStoredToken, hasValidToken, clearAuthData } from './token';
import { AuthError, AuthErrorCode, LoginResponse } from '@/types/auth';

// Base API configuration
const API_BASE_URL = '/api/dawa_proxy';

/**
 * Create authenticated headers for API requests
 */
const createAuthHeaders = (): HeadersInit => {
  const token = getStoredToken();
  
  console.log('🔍 Creating auth headers:', {
    hasToken: !!token,
    tokenLength: token?.length || 0,
    tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
    timestamp: new Date().toISOString()
  });
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
  
  console.log('✅ Auth headers created:', {
    headers: headers,
    hasAuthorization: !!headers.Authorization,
    timestamp: new Date().toISOString()
  });
  
  return headers;
};

/**
 * Handle API response and check for authentication errors
 */
const handleApiResponse = async (response: Response): Promise<any> => {
  if (response.status === 401) {
    // Token is invalid or expired
    clearAuthData();
    throw new AuthError({
      code: AuthErrorCode.TOKEN_EXPIRED,
      message: 'Authentication token has expired. Please log in again.',
    });
  }

  if (!response.ok) {
    const errorText = await response.text();
    let errorData;
    
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { message: errorText };
    }

    throw new AuthError({
      code: AuthErrorCode.SERVER_ERROR,
      message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
      details: errorData,
    });
  }

  // Try to parse JSON response
  try {
    return await response.json();
  } catch {
    // Return text if not JSON
    return await response.text();
  }
};

/**
 * Make an authenticated API request
 */
export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  // Check if we have a valid token before making the request
  if (!hasValidToken()) {
    clearAuthData();
    throw new AuthError({
      code: AuthErrorCode.TOKEN_EXPIRED,
      message: 'No valid authentication token found. Please log in again.',
    });
  }

  const url = `${API_BASE_URL}?endpoint=${encodeURIComponent(endpoint)}`;
  
  const requestOptions: RequestInit = {
    ...options,
    headers: {
      ...createAuthHeaders(),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, requestOptions);
    return await handleApiResponse(response);
  } catch (error) {
    if (error instanceof AuthError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new AuthError({
        code: AuthErrorCode.NETWORK_ERROR,
        message: 'Network error. Please check your connection and try again.',
      });
    }

    throw new AuthError({
      code: AuthErrorCode.UNKNOWN_ERROR,
      message: error instanceof Error ? error.message : 'An unknown error occurred',
    });
  }
};

/**
 * Make a GET request
 */
export const apiGet = <T = any>(endpoint: string): Promise<T> => {
  return apiRequest<T>(endpoint, { method: 'GET' });
};

/**
 * Make a POST request
 */
export const apiPost = <T = any>(endpoint: string, data?: any): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * Make a PUT request
 */
export const apiPut = <T = any>(endpoint: string, data?: any): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * Make a DELETE request
 */
export const apiDelete = <T = any>(endpoint: string): Promise<T> => {
  return apiRequest<T>(endpoint, { method: 'DELETE' });
};

/**
 * Make a request to the login endpoint
 */
export const loginRequest = async (credentials: {
  email: string;
  password: string;
  rememberMe?: boolean;
}): Promise<LoginResponse> => {
  const url = `${API_BASE_URL}?endpoint=${encodeURIComponent('/Account/login')}`;
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok || !data.successful) {
      throw new AuthError({
        code: AuthErrorCode.INVALID_CREDENTIALS,
        message: data.error || 'Invalid email or password',
        details: data,
      });
    }

    return data;
  } catch (error) {
    if (error instanceof AuthError) {
      throw error;
    }

    throw new AuthError({
      code: AuthErrorCode.NETWORK_ERROR,
      message: 'Network error during login. Please check your connection and try again.',
    });
  }
};

/**
 * Make a request to the forgot password endpoint
 */
export const forgotPasswordRequest = async (email: string): Promise<any> => {
  const url = `${API_BASE_URL}?endpoint=${encodeURIComponent('/Account/forgotpw')}&email=${encodeURIComponent(email)}`;
  
  console.log('🔍 Forgot password request starting:', {
    email,
    url,
    timestamp: new Date().toISOString()
  });
  
  try {
    console.log('📤 Sending forgot password request:', {
      url,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: 'empty'
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: '', // Empty body as required by the API
    });

    console.log('📥 Received forgot password response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
      timestamp: new Date().toISOString()
    });

    const data = await response.json();
    console.log('📋 Parsed response data:', {
      data,
      timestamp: new Date().toISOString()
    });

    if (!response.ok) {
      console.error('❌ Forgot password request failed:', {
        status: response.status,
        statusText: response.statusText,
        data,
        timestamp: new Date().toISOString()
      });
      
      throw new AuthError({
        code: AuthErrorCode.SERVER_ERROR,
        message: data.error || 'Failed to send password reset email',
        details: data,
      });
    }

    console.log('✅ Forgot password request successful:', {
      data,
      timestamp: new Date().toISOString()
    });

    return data;
  } catch (error) {
    console.error('💥 Forgot password request exception:', {
      error,
      errorType: error?.constructor?.name,
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      timestamp: new Date().toISOString()
    });

    if (error instanceof AuthError) {
      throw error;
    }

    throw new AuthError({
      code: AuthErrorCode.NETWORK_ERROR,
      message: 'Network error. Please check your connection and try again.',
    });
  }
};

/**
 * Make a request to the reset password endpoint
 */
export const resetPasswordRequest = async (userId: string, code: string, password: string): Promise<any> => {
  const url = `${API_BASE_URL}?endpoint=${encodeURIComponent('/Account/resetpw')}`;
  
  console.log('🔍 Reset password request starting:', {
    userId,
    code: code.substring(0, 20) + '...', // Log partial code for security
    url,
    timestamp: new Date().toISOString()
  });
  
  try {
    const requestBody = {
      userId,
      code,
      password
    };

    console.log('📤 Sending reset password request:', {
      url,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { userId, code: '***', password: '***' } // Don't log sensitive data
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('📥 Received reset password response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
      timestamp: new Date().toISOString()
    });

    const data = await response.json();
    console.log('📋 Parsed response data:', {
      data,
      timestamp: new Date().toISOString()
    });

    if (!response.ok || !data.successful) {
      console.error('❌ Reset password request failed:', {
        status: response.status,
        statusText: response.statusText,
        data,
        timestamp: new Date().toISOString()
      });
      
      throw new AuthError({
        code: AuthErrorCode.SERVER_ERROR,
        message: data.error || 'Failed to reset password',
        details: data,
      });
    }

    console.log('✅ Reset password request successful:', {
      data,
      timestamp: new Date().toISOString()
    });

    return data;
  } catch (error) {
    console.error('💥 Reset password request exception:', {
      error,
      errorType: error?.constructor?.name,
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      timestamp: new Date().toISOString()
    });

    if (error instanceof AuthError) {
      throw error;
    }

    throw new AuthError({
      code: AuthErrorCode.NETWORK_ERROR,
      message: 'Network error. Please check your connection and try again.',
    });
  }
};

/**
 * Validate token with the server
 */
export const validateToken = async (): Promise<boolean> => {
  try {
    // You can implement a token validation endpoint here
    // For now, we'll just check if the token exists and isn't expired
    return hasValidToken();
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
}; 